import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import useAuth from "../../hooks/useAuth";
import {
  getDashboardInfo,
  getTotalStudent,
  getFathersProfessionSummary,
  getClassWiseStudentSummary,
} from "../../api/dashboardApi";
import { getSmsBalance } from "../../api/smsApi";
import { getInstituteProfile } from "../../api/instituteApi";
import { createLookupApi } from "../../api/lookupApi";
import "./Dashboard.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's real "dashboard" page (`dashboard.html`,
 * reached via the sidebar's own "dashboard" link — distinct from `/home`,
 * the actual post-login landing page, which now has its own
 * `pages/Home/HomePage.js`). Ports every *real*, backend-driven section of
 * that page:
 *  - staff/class/section/group/shift counts, today's & this month's
 *    income/expense (Phase 11)
 *  - father's-occupation breakdown (Phase 11) — same 11 categories the old
 *    page actually shows (the other ~12 the API also returns are wrapped in
 *    an HTML comment in the old template, i.e. deliberately hidden there too)
 *  - Total Student card with an academic-year picker (old app: a multiselect
 *    defaulting to the current year; here a plain multi-select, same default)
 *  - SMS balance
 *  - Class Wise Student Chart (old app: a Chart.js bar chart; here a
 *    dependency-free CSS bar list — same real per-class counts, no new
 *    charting library added for one chart)
 * Deliberately NOT ported — decorative/fake in the old page itself, never
 * wired to a real endpoint there either: the "Today's Birthday" count
 * (hardcoded "25"), "Student Religion Information" table (hardcoded zeros),
 * "Student/Staff Fees Collection" cards and the "User-wise"/"Ledger-wise"/
 * "Class-wise"/"Fee-wise Collected Amount" tables (hardcoded zeros and, in
 * one case, literal fake employee names — dead placeholder markup, not a
 * real feature to preserve).
 * Also NOT ported (real, but each belongs to an already-scoped-and-deferred
 * bigger module, not something to silently fold in here): the daily
 * section-wise/teacher attendance summary tables (Phase 6's ~30 deferred
 * attendance report variants) and the exam pass/fail-by-year-and-exam
 * summary with its 2 pie charts (Phase 7's deferred merit/report
 * controllers) — see MIGRATION.md.
 */
export default function Dashboard() {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [professions, setProfessions] = useState(null);
  const [classSummary, setClassSummary] = useState(null);
  const [smsBalance, setSmsBalance] = useState(null);

  const [academicYears, setAcademicYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  const [studentTotals, setStudentTotals] = useState(null);

  useEffect(() => {
    getDashboardInfo().then(setInfo);
    getFathersProfessionSummary().then(setProfessions);
    getClassWiseStudentSummary().then(setClassSummary);
    getSmsBalance()
      .then((b) => setSmsBalance(b.balance))
      .catch(() => setSmsBalance(null));
    Promise.all([academicYearApi.list(), getInstituteProfile()]).then(([years, profile]) => {
      setAcademicYears(years);
      const current = years.find((y) => y.name === profile.academicYear);
      setSelectedYears(current ? [current.name] : years.length ? [years[0].name] : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedYears.length === 0) return;
    getTotalStudent(selectedYears).then(setStudentTotals);
  }, [selectedYears]);

  const professionRows = professions
    ? Object.entries(professions.counts)
        // Same subset the old dashboard.html actually renders (the rest are
        // fetched but sit inside a commented-out block there too).
        .filter(([profession]) =>
          [
            "Teacher",
            "Student",
            "Service",
            "Retired Person",
            "Private Service",
            "Police Officer",
            "Phycian",
            "Navy",
            "NSI",
            "NRB",
            "N/A",
          ].includes(profession)
        )
        .map(([profession, count]) => ({ profession, count }))
        .sort((a, b) => b.count - a.count)
    : [];

  const maxClassCount = classSummary ? Math.max(1, ...classSummary.map((c) => c.totalStudent)) : 1;

  return (
    <MainLayout>
      <h1>Welcome{user && user.nickName ? `, ${user.nickName}` : ""}</h1>
      {user && (
        <p style={{ color: "var(--color-text-muted)" }}>
          {user.instituteName} — {user.roleName}
        </p>
      )}

      {!info ? (
        <p>Loading...</p>
      ) : (
        <>
          <div className="dashboard-section-title">Staff</div>
          <div className="dashboard-cards">
            <Card label="Total Staff" value={info.totalHr} />
            <Card label="Male" value={info.totalMaleHr} />
            <Card label="Female" value={info.totalFemaleHr} />
            <Card label="Others" value={info.totalOthersHr} />
          </div>

          <div className="dashboard-section-title">Setup</div>
          <div className="dashboard-cards">
            <Card label="Classes" value={info.totalClass} />
            <Card label="Groups" value={info.totalGroup} />
            <Card label="Sections" value={info.totalSection} />
            <Card label="Shifts" value={info.totalShift} />
          </div>

          <div className="dashboard-section-title">Accounts</div>
          <div className="dashboard-cards">
            <Card label="Today's Income" value={info.totalIncome} />
            <Card label="Today's Expense" value={info.totalExpense} />
            <Card label="This Month's Income" value={info.totalMonthlyIncome} />
            <Card label="This Month's Expense" value={info.totalMonthlyExpense} />
          </div>

          <div className="dashboard-section-title">SMS</div>
          <div className="dashboard-cards">
            <Card label="SMS Balance" value={smsBalance === null ? "—" : smsBalance} />
          </div>
        </>
      )}

      <div className="dashboard-section-title">Total Student</div>
      <div className="dashboard-year-picker">
        <label htmlFor="dashboard-year-select">Academic Year</label>
        <select
          id="dashboard-year-select"
          multiple
          value={selectedYears}
          onChange={(e) => setSelectedYears(Array.from(e.target.selectedOptions, (o) => o.value))}
        >
          {academicYears.map((y) => (
            <option key={y.id} value={y.name}>
              {y.name}
            </option>
          ))}
        </select>
      </div>
      {studentTotals && (
        <div className="dashboard-cards">
          <Card label="Total Students" value={studentTotals.totalStudent} />
          <Card label="Male" value={studentTotals.totalMaleStudent} />
          <Card label="Female" value={studentTotals.totalFemaleStudent} />
          <Card label="Others" value={studentTotals.totalOthersStudent} />
        </div>
      )}

      {classSummary && classSummary.length > 0 && (
        <>
          <div className="dashboard-section-title">Class Wise Student Chart</div>
          <div className="class-chart">
            {classSummary.map((c) => (
              <div className="class-chart-row" key={c.classId}>
                <div className="class-chart-label">{c.className}</div>
                <div className="class-chart-track">
                  <div
                    className="class-chart-bar"
                    style={{ width: `${(c.totalStudent / maxClassCount) * 100}%`, background: c.color }}
                  />
                </div>
                <div className="class-chart-count">{c.totalStudent}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {professionRows.length > 0 && (
        <>
          <div className="dashboard-section-title">Father's Profession Summary</div>
          <DataTable
            columns={[
              { key: "profession", label: "Profession" },
              { key: "count", label: "Number of Students" },
            ]}
            rows={professionRows}
          />
        </>
      )}
    </MainLayout>
  );
}

function Card({ label, value }) {
  return (
    <div className="dashboard-card">
      <div className="value">{value}</div>
      <div className="label">{label}</div>
    </div>
  );
}
